// 성과지표 현황

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import SidePanel from '@/components/ui/SidePanel'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { PerformanceService } from '@/api/generated/services/PerformanceService'
import type { BusinessData } from '@/api/generated/models/BusinessData'
import type { PerformanceCategoryData } from '@/api/generated/models/PerformanceCategoryData'
import type { PerformanceClassData } from '@/api/generated/models/PerformanceClassData'
import type { PerformanceData } from '@/api/generated/models/PerformanceData'

// 그리드에 표시할 플랫 데이터 구조
interface PerformanceClassRow {
  id: string
  performanceGroupName: string // 지표구분명
  categoryHierarchy: string // 하위분류 체계 (상위 > 하위)
  name: string // 성과지표명
  formula: string // 수식 (목표수치 수식 또는 달성수치 수식)
  targetFigure: number // 목표값
  doneFigure: number // 달성값
  figurePercentage: number // 달성율
  // 원본 데이터 참조
  originalClass: PerformanceClassData
  performances: PerformanceData[]
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

export default function PerformanceDashboardPage() {
  // PerformanceManagement와 동일한 방식으로 selectedWorkspace 사용
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  const [businessDataList, setBusinessDataList] = useState<BusinessData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Side Panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [selectedClass, setSelectedClass] = useState<PerformanceClassRow | null>(null)

  // API 데이터 가져오기
  const fetchPerformanceData = useCallback(async () => {
    if (!selectedWorkspace?.id) return

    setLoading(true)
    setError(null)

    try {
      const data = await PerformanceService.appsPerformanceViewsGetWorkspacePerformanceAll({
        workspaceId: selectedWorkspace.id
      })
      console.log('PerformanceDashboard API 응답:', data)
      setBusinessDataList(data)
    } catch (err) {
      console.error('Failed to fetch performance data:', err)
      setError('성과지표 데이터를 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [selectedWorkspace?.id])

  useEffect(() => {
    fetchPerformanceData()
  }, [fetchPerformanceData])

  // 재귀적으로 모든 카테고리에서 클래스 추출
  const extractClassesFromCategory = useCallback((
    category: PerformanceCategoryData,
    groupName: string,
    parentPath: string[] = []
  ): PerformanceClassRow[] => {
    const rows: PerformanceClassRow[] = []
    const currentPath = [...parentPath, category.name]
    const categoryHierarchy = currentPath.join(' > ')

    // 현재 카테고리의 클래스들 추출
    if (category.performance_classes && category.performance_classes.length > 0) {
      category.performance_classes.forEach(cls => {
        rows.push({
          id: cls.id,
          performanceGroupName: groupName,
          categoryHierarchy,
          name: cls.name,
          formula: cls.target_figure_formula || cls.done_figure_formula || '-',
          targetFigure: cls.target_figure,
          doneFigure: cls.done_figure,
          figurePercentage: cls.figure_percentage,
          originalClass: cls,
          performances: cls.performances || []
        })
      })
    }

    // 하위 카테고리 재귀 처리
    if (category.performance_categories && category.performance_categories.length > 0) {
      category.performance_categories.forEach(subCategory => {
        rows.push(...extractClassesFromCategory(subCategory, groupName, currentPath))
      })
    }

    return rows
  }, [])

  // BusinessData에서 성과지표 행 데이터 추출
  const extractRowsFromBusiness = useCallback((business: BusinessData): PerformanceClassRow[] => {
    const rows: PerformanceClassRow[] = []

    if (!business.performance_groups) return rows

    business.performance_groups.forEach(group => {
      if (group.performance_categories) {
        group.performance_categories.forEach(category => {
          rows.push(...extractClassesFromCategory(category, group.name))
        })
      }
    })

    return rows
  }, [extractClassesFromCategory])

  // 값 포맷팅 함수들
  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat('ko-KR').format(value)
  }

  const formatRate = (rate: number | null | undefined): string => {
    if (rate === null || rate === undefined) return '-'
    return `${rate.toFixed(1)}%`
  }

  const getAchievementRateColor = (rate: number | null | undefined): string => {
    if (rate === null || rate === undefined) return designTokens.colors.gray[6]
    if (rate >= 100) return designTokens.colors.success.DEFAULT
    if (rate >= 80) return designTokens.colors.primary.DEFAULT
    if (rate >= 60) return designTokens.colors.warning.DEFAULT
    return designTokens.colors.error.DEFAULT
  }

  // 행 클릭 핸들러
  const handleRowClick = useCallback((row: PerformanceClassRow) => {
    setSelectedClass(row)
    setIsPanelOpen(true)
  }, [])

  // 사이드 패널 닫기
  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false)
    setSelectedClass(null)
  }, [])

  // DataGrid 컬럼 정의
  const columnDefs: ColDef<PerformanceClassRow>[] = useMemo(() => [
    {
      headerName: '지표구분',
      field: 'performanceGroupName',
      width: 150,
      sortable: true,
      filter: false
    },
    {
      headerName: '하위분류 체계',
      field: 'categoryHierarchy',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: false
    },
    {
      headerName: '성과지표명',
      field: 'name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: false
    },
    {
      headerName: '수식',
      field: 'formula',
      width: 150,
      sortable: false,
      filter: false,
      cellStyle: { color: designTokens.colors.gray[4] }
    },
    {
      headerName: '목표값',
      field: 'targetFigure',
      width: 120,
      sortable: true,
      filter: false,
      valueFormatter: params => formatValue(params.value),
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: '달성값',
      field: 'doneFigure',
      width: 120,
      sortable: true,
      filter: false,
      valueFormatter: params => formatValue(params.value),
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: '달성율',
      field: 'figurePercentage',
      width: 100,
      sortable: true,
      filter: false,
      valueFormatter: params => formatRate(params.value),
      cellStyle: (params) => ({
        textAlign: 'right',
        color: getAchievementRateColor(params.value),
        fontWeight: 600
      })
    }
  ], [])

  // 세부지표 목록 컬럼 정의 (사이드 패널용)
  const performanceColumnDefs: ColDef<PerformanceData>[] = useMemo(() => [
    {
      headerName: '세부지표명',
      field: 'name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: false
    },
    {
      headerName: '유형',
      field: 'link_type',
      width: 120,
      sortable: true,
      filter: false,
      cellRenderer: (params: ICellRendererParams<PerformanceData>) => {
        const linkType = params.value
        const label = linkType === 'task' ? '연결세부지표' : '세부지표'
        const color = linkType === 'task' ? 'warning' as const : 'error' as const
        return (
          <Tag color={color} size="small">
            {label}
          </Tag>
        )
      }
    },
    {
      headerName: '목표값',
      field: 'target_figure',
      width: 100,
      sortable: true,
      filter: false,
      valueFormatter: params => formatValue(params.value),
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: '달성값',
      field: 'done_figure',
      width: 100,
      sortable: true,
      filter: false,
      valueFormatter: params => formatValue(params.value),
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: '달성율',
      field: 'figure_percentage',
      width: 100,
      sortable: true,
      filter: false,
      valueFormatter: params => formatRate(params.value),
      cellStyle: (params) => ({
        textAlign: 'right',
        color: getAchievementRateColor(params.value),
        fontWeight: 600
      })
    }
  ], [])

  // Loading state
  if (loading) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.gray[4]
          }}
        >
          성과지표 데이터를 불러오는 중...
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.error.DEFAULT
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto space-y-8">
        {businessDataList.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: designTokens.colors.gray[4]
              }}
            >
              표시할 성과지표 데이터가 없습니다.
            </div>
          </div>
        ) : (
          businessDataList.map(business => {
            const rows = extractRowsFromBusiness(business)

            return (
              <div key={business.id} className="space-y-4">
                {/* 사업명 타이틀 */}
                <h2
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['title-medium'][0],
                    lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                    fontWeight: 700,
                    color: designTokens.colors.gray[6],
                    paddingBottom: '8px',
                    borderBottom: `2px solid ${designTokens.colors.primary.DEFAULT}`
                  }}
                >
                  {business.name}
                </h2>

                {/* DataGrid or Empty State */}
                {rows.length === 0 ? (
                  <div
                    className="flex items-center justify-center py-12 border rounded"
                    style={{
                      borderColor: designTokens.colors.gray[1],
                      backgroundColor: designTokens.colors.gray[0]
                    }}
                  >
                    <span
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: designTokens.typography.fontSize['body-medium'][0],
                        color: designTokens.colors.gray[4]
                      }}
                    >
                      등록된 성과지표가 없습니다.
                    </span>
                  </div>
                ) : (
                  <DataGrid
                    rowData={rows}
                    columnDefs={columnDefs}
                    height={Math.min(400, 52 * (rows.length + 1) + 48)}
                    rowSelection="single"
                    animateRows={true}
                    headerHeight={48}
                    rowHeight={52}
                    enableCellCopy={true}
                    onRowClicked={(event) => {
                      if (event.data) {
                        handleRowClick(event.data)
                      }
                    }}
                    getRowId={(params) => params.data.id}
                  />
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Side Panel - 세부지표 목록 */}
      <SidePanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        title="성과지표 상세"
        width={900}
      >
        {selectedClass && (
          <div
            className="flex flex-col space-y-6"
            style={{
              padding: '24px',
            }}
          >
            {/* 성과지표 기본정보 */}
            <div className="space-y-2">
              <SectionHeader title="성과지표 정보" />
              <div className="space-y-4" style={{ padding: '0px 4px' }}>
                <InfoField
                  label="지표구분"
                  value={selectedClass.performanceGroupName}
                />
                <InfoField
                  label="하위분류 체계"
                  value={selectedClass.categoryHierarchy}
                />
                <InfoField
                  label="성과지표명"
                  value={selectedClass.name}
                />
                {selectedClass.formula !== '-' && (
                  <InfoField
                    label="수식"
                    value={selectedClass.formula}
                  />
                )}
                <InfoField
                  label="목표값"
                  value={formatValue(selectedClass.targetFigure)}
                />
                <InfoField
                  label="달성값"
                  value={formatValue(selectedClass.doneFigure)}
                />
                <InfoField
                  label="달성율"
                  value={
                    <span
                      style={{
                        color: getAchievementRateColor(selectedClass.figurePercentage),
                        fontWeight: 600
                      }}
                    >
                      {formatRate(selectedClass.figurePercentage)}
                    </span>
                  }
                />
              </div>
            </div>

            {/* 세부지표 목록 */}
            <div className="space-y-2">
              <SectionHeader title="세부지표 목록" />
              {selectedClass.performances.length === 0 ? (
                <div
                  className="flex items-center justify-center py-8"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: designTokens.colors.gray[4]
                  }}
                >
                  등록된 세부지표가 없습니다.
                </div>
              ) : (
                <DataGrid
                  rowData={selectedClass.performances}
                  columnDefs={performanceColumnDefs}
                  height={Math.min(400, 42 * (selectedClass.performances.length + 1) + 40)}
                  rowSelection="single"
                  animateRows={true}
                  headerHeight={40}
                  rowHeight={42}
                  enableCellCopy={true}
                  getRowId={(params) => params.data.id}
                />
              )}
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  )
}
